// app/src/lib/db.js — Dexie v2 schema (SPECIFICATION.md §4.2).
//
// Conventions:
//   - All IDs are string UUIDv4 unless noted.
//   - Monetary values stored as integer minor units (cents) to avoid float error.
//   - All entity rows include `userId` (Cognito sub) for tenant isolation; the Dexie
//     instance is keyed per browser profile and per origin so cross-tenant leakage
//     within IDB is structurally impossible, but `userId` is still recorded for
//     downstream verification against backup metadata.

import Dexie from 'dexie';

/**
 * Canonical schema entities — see SPECIFICATION.md §4 for the full per-field contract.
 *
 * @typedef {Object} UserProfile
 * @property {string} userId
 * @property {string} businessName
 * @property {string} timezone
 * @property {string} currency       - ISO 4217, e.g. "USD".
 * @property {string} locale         - BCP-47, e.g. "en-US".
 * @property {number} createdAt
 *
 * @typedef {Object} Vendor
 * @property {string} vendorId
 * @property {string} userId
 * @property {string} name
 * @property {string=} normalizedName
 * @property {string=} terms          - "Net 15", etc.
 *
 * @typedef {Object} Invoice
 * @property {string} invoiceId
 * @property {string} userId
 * @property {string} vendorId
 * @property {string=} invoiceNumber
 * @property {string=} poNumber
 * @property {string} issuedDate      - YYYY-MM-DD.
 * @property {string=} dueDate
 * @property {string=} terms
 * @property {number} subtotalCents
 * @property {number} taxCents
 * @property {number=} freightCents
 * @property {number} totalCents
 * @property {"DRAFT"|"RECEIVED"|"PAID_LOCAL"|"CONFIRMED"|"DISPUTED"|"OVERDUE"} status
 * @property {string=} plaidTxId
 * @property {string=} plaidAccountId
 * @property {boolean=} isDuplicate
 * @property {string=} duplicateOfId
 * @property {string=} receiptObjectKey   - S3 key (no URL).
 * @property {number} createdAt
 *
 * @typedef {Object} LineItem
 * @property {string} lineItemId
 * @property {string} userId
 * @property {string} invoiceId
 * @property {string=} sku
 * @property {string} description
 * @property {number} quantity
 * @property {string=} uom
 * @property {number} unitPriceCents
 * @property {number} extendedPriceCents
 * @property {number=} discountCents
 * @property {string=} lotNumber
 * @property {boolean=} taxable
 * @property {number=} confidence      - 0..1.
 *
 * @typedef {Object} SalesEntry
 * @property {string} salesEntryId
 * @property {string} userId
 * @property {string} date            - YYYY-MM-DD.
 * @property {"POS"|"DELIVERY"|"CATERING"|"OTHER"} channel
 * @property {number} netSalesCents
 * @property {number=} taxCents
 * @property {number=} tipsCents
 *
 * @typedef {Object} RecurringItem
 * @property {string} recurringId
 * @property {string} userId
 * @property {string} label
 * @property {number} amountCents
 * @property {"WEEKLY"|"BIWEEKLY"|"MONTHLY"} cadence
 * @property {string} nextDate
 *
 * @typedef {Object} PlaidItem
 * @property {string} plaidItemId
 * @property {string} userId
 * @property {string} institutionName
 * @property {string=} institutionLogo
 * @property {"HEALTHY"|"ITEM_LOGIN_REQUIRED"|"PENDING_EXPIRATION"|"REVOKED"} status
 * @property {number} lastSyncedAt
 *
 * @typedef {Object} PlaidAccount
 * @property {string} plaidAccountId
 * @property {string} userId
 * @property {string} plaidItemId
 * @property {string} name
 * @property {string} type            - depository | credit | ...
 * @property {string} subtype
 * @property {string} mask
 *
 * @typedef {Object} PlaidTransaction
 * @property {string} plaidTxId
 * @property {string} userId
 * @property {string} plaidItemId
 * @property {string} plaidAccountId
 * @property {string} date            - YYYY-MM-DD.
 * @property {number} amountCents     - signed; positive = outflow.
 * @property {boolean} pending
 * @property {string} merchantName
 * @property {string=} category
 *
 * @typedef {Object} Alert
 * @property {string} alertId
 * @property {string} userId
 * @property {"info"|"warning"|"critical"} severity
 * @property {string} kind            - e.g. "PRICE_SPIKE" | "DUPLICATE_INVOICE".
 * @property {string} title
 * @property {string=} body
 * @property {number} createdAt
 * @property {boolean=} dismissed
 *
 * @typedef {Object} AppliedWalCheckpoint
 * @property {string} walKey          - S3 object key.
 * @property {number} appliedAt
 */

export const db = new Dexie('kanjo');

db.version(1).stores({
  // v1 placeholder so v2 has a from-state for migration.
  userProfile: 'userId',
});

db.version(2).stores({
  userProfile:           'userId',
  vendors:               'vendorId, userId, normalizedName, [userId+name]',
  invoices:              'invoiceId, userId, vendorId, status, issuedDate, dueDate, plaidTxId, [vendorId+issuedDate], [userId+status]',
  lineItems:             'lineItemId, userId, invoiceId, sku, [invoiceId+sku]',
  salesEntries:          'salesEntryId, userId, date, channel, [userId+date]',
  recurringItems:        'recurringId, userId, nextDate',
  plaidItems:            'plaidItemId, userId, status, [userId+status]',
  plaidAccounts:         'plaidAccountId, userId, plaidItemId',
  plaidTransactions:     'plaidTxId, userId, plaidAccountId, plaidItemId, date, pending, [userId+date]',
  alerts:                'alertId, userId, severity, kind, dismissed, [userId+dismissed+severity]',
  appliedWalCheckpoints: 'walKey, appliedAt',
}).upgrade(async (_tx) => {
  // No data migration required from v1 placeholder; future versions can transform here.
});

// ── v3: Phase 2 procurement entities ──────────────────────────────────────
//
// CONVENTION NOTE: We keep Phase 1's "string UUID primary keys + userId on every row"
// convention rather than the spec's ++id auto-increment style. Reasons:
//   1. UUIDs avoid info leakage from sequential IDs in shared/restored snapshots.
//   2. userId-on-row enables defense-in-depth filtering at query time, even though
//      Dexie's per-origin scoping already isolates tenants.
//   3. Backup blobs are portable across devices (no auto-increment collision risk).
// IMPORTANT: Dexie versions are additive. Do not edit older `version()` blocks.
db.version(3).stores({
  // Extend vendors to add new index for active-only listings.
  vendors:               'vendorId, userId, normalizedName, isActive, [userId+name], [userId+isActive]',
  // New entities.
  items:                 'itemId, userId, internalSku, normalizedName, category, isActive, [userId+isActive], [userId+category]',
  vendorItems:           'vendorItemId, userId, vendorId, itemId, isActive, [vendorId+itemId], [vendorId+isActive]',
  priceEntries:          'priceEntryId, userId, vendorId, itemId, effectiveDate, source, [vendorId+itemId+effectiveDate], [itemId+effectiveDate]',
  vendorContracts:       'contractId, userId, vendorId, itemId, effectiveFrom, effectiveThrough, [vendorId+itemId], [userId+effectiveThrough]',
  parItems:              'parItemId, userId, itemId, preferredVendorId, [userId+itemId]',
  purchaseOrders:        'poId, userId, vendorId, status, orderDate, [userId+status], [vendorId+orderDate]',
  poLineItems:           'poLineId, userId, poId, itemId, [poId+itemId]',
  deliverySlips:         'slipId, userId, vendorId, invoiceId, deliveryDate, status, [userId+status], [vendorId+deliveryDate]',
  deliveryLineItems:     'slipLineId, userId, slipId, invoiceLineId, [slipId+itemId]',
}).upgrade(async (_tx) => {
  // No data migration — Phase 1 stores untouched.
});

// (Phase 1 schema definitions continue below; see version blocks ascending.)

// ── v4: Phase 3 cashflow ──────────────────────────────────────────────────
db.version(4).stores({
  // Account balances + per-account-type tagging for cash position breakdown.
  // currentBalanceCents and availableBalanceCents are NOT indexed; the index list
  // names only the keys we actually query by. Extra fields live on the row.
  plaidAccounts:         'plaidAccountId, userId, plaidItemId, type, [userId+type]',
  // Manual accounts-receivable entries (no automation in Phase 3 MVP).
  arEntries:             'arId, userId, customerName, expectedDate, status, plaidTxId, [userId+status], [userId+expectedDate]',
  // Manually-entered payroll events (used by forecast).
  payrollEvents:         'payrollId, userId, payDate, [userId+payDate]',
  // Per-user cashflow preferences (gap threshold, push opt-in, etc.).
  cashflowPrefs:         'userId',
  // Recurring detection: candidate streams that the user has not yet confirmed.
  recurringCandidates:   'candidateId, userId, merchantNorm, cadence, status, [userId+status]',
}).upgrade(async (_tx) => {
  // No transformations needed; new tables only.
});

// ── v5: Phase 4 revenue & margin ──────────────────────────────────────────
db.version(5).stores({
  // Channels are user-defined revenue lines (DoorDash, dine-in, retail, etc).
  // commissionRateBps stores basis points so commission math stays in integers
  // (30% = 3000 bps). plaidAccountId links optional bank account for auto-close.
  channels:              'channelId, userId, name, type, isActive, plaidAccountId, [userId+isActive], [userId+type]',
  // Extend salesEntries with channelId + a per-channel breakdown of gross/fees/tips/refunds/voids/comps.
  // The base salesEntries store from v2 is retained; existing rows are upgraded by adding the new index.
  salesEntries:          'salesEntryId, userId, date, channelId, [userId+date], [userId+channelId+date]',
  // Recipe = menu item; recipeIngredients references items.itemId.
  recipes:               'recipeId, userId, name, isActive, [userId+isActive]',
  recipeIngredients:     'ingredientId, userId, recipeId, itemId, [recipeId+itemId]',
  // Waste entries valued at the current price at time of entry.
  wasteEntries:          'wasteId, userId, itemId, date, reason, [userId+date], [userId+itemId+date]',
  // Sales tax jurisdictions + payments.
  taxJurisdictions:      'jurisdictionId, userId, name, filingFrequency, nextFilingDate, [userId+nextFilingDate]',
  taxPayments:           'taxPaymentId, userId, jurisdictionId, paidDate, [userId+jurisdictionId+paidDate]',
  // 1099 tracking. TIN is encrypted-at-rest as a base64url envelope; never stored plaintext.
  vendorTaxInfo:         'taxInfoId, userId, vendorId, taxYear, needs1099, [userId+taxYear], [vendorId+taxYear]',
}).upgrade(async (_tx) => {
  // Existing salesEntries rows lose nothing — the schema is additive.
});

// ── v6: Phase 5 scale & monetization ──────────────────────────────────────
db.version(6).stores({
  // Plan tier + trial window per owner (single row keyed by userId).
  planState:             'userId',
  // Members of a business + their role. ownerId = original account holder's Cognito sub;
  // memberId = the invitee's Cognito sub (resolved at sign-in).
  members:               'memberRowId, ownerId, memberId, role, status, expiresAt, [ownerId+status], [memberId+ownerId]',
  invitations:           'invitationId, ownerId, emailHash, role, status, createdAt, [ownerId+status]',
  // Audit log: append-only. There is no `update` method on this table; rows are immutable.
  auditEntries:          'auditId, ownerId, actorId, ts, action, entityType, entityId, [ownerId+ts], [entityType+entityId]',
  // Approval requests for POs / invoice payments above the configured threshold.
  approvalRequests:      'requestId, ownerId, status, entityType, entityId, requesterId, createdAt, [ownerId+status]',
  // Integration metadata only. Plaintext tokens live in Secrets Manager server-side.
  integrations:          'integrationId, ownerId, provider, status, [ownerId+provider]',
  // Outbound webhook subscriptions and per-delivery log.
  webhookSubscriptions:  'subscriptionId, ownerId, url, status, [ownerId+status]',
  webhookDeliveries:     'deliveryId, subscriptionId, ts, status, [subscriptionId+ts]',
}).upgrade(async (_tx) => {
  // Seeded by application code on first sign-in.
});

// ── v7: Delivery-platform statements (Pro) ────────────────────────────────
db.version(7).stores({
  // One row per uploaded monthly platform statement. `uploadMonth` (YYYY-MM of
  // createdAt) backs the "3 uploads/month" quota; `periodMonth` is the
  // statement's own reporting month (what the figures cover). Money fields live
  // on the row but aren't indexed.
  deliveryStatements:    'statementId, userId, platform, periodMonth, uploadMonth, [userId+periodMonth], [userId+uploadMonth]',
}).upgrade(async (_tx) => {
  // New table only; nothing to migrate.
});

// ── v8: cached scan blobs so the receipt detail can show the original ──────
// Storing the blob locally is the simplest source — it survives page reloads
// (Dexie persists), uses no extra S3 fetch, and lets the user spot OCR errors
// against the paper they actually have. The `invoiceId` index lets the detail
// screen find the right blob without needing the upload's receiptId.
db.version(8).stores({
  receiptImages:         'receiptId, invoiceId, userId, createdAt, [userId+createdAt]',
}).upgrade(async (_tx) => {
  // New table only; pre-existing invoices have no cached image (the screen
  // shows a "not stored locally" placeholder for those).
});

// ── v9: maintenance service providers (plumbers, HVAC, hood cleaning, …) ──
// The blob column holds the captured business-card photo (optional). All
// other fields are manual entry. `shareWithArea` is the per-provider opt-in
// for sharing into the area pool (server-side wiring lands in a follow-up).
db.version(9).stores({
  maintenanceProviders:  'providerId, userId, category, rating, [userId+category]',
}).upgrade(async (_tx) => {
  // New table only.
});

// ── v10: landlord CAM reconciliation statements (annual) ───────────────────
// These are CONTEXT, not transactions — the actual rent + NNN outflows still
// come from bank activity, but the statement tells the operator what each
// dollar of NNN actually paid for so they can spot which line item (CAM,
// property tax, insurance, utilities) is driving year-over-year drift. One
// row per statement, indexed by userId + statementYear for the YoY view.
db.version(10).stores({
  landlordStatements:    'statementId, userId, statementYear, [userId+statementYear]',
}).upgrade(async (_tx) => {
  // New table only.
});

// ── v11: drop operator-sales-tax stores ─────────────────────────────────────
// `taxJurisdictions` and `taxPayments` were scaffolded for an operator-side
// sales-tax tracking feature that was never wired to any UI. Kanjo handles
// sales tax only for its own SaaS subscription billing in WA (see
// infra/lambdas/tax-rate-lookup); it does not handle the operator's own
// customer sales tax. The dead stores are removed so fresh installs don't
// carry inert tables; Dexie drops them in-place on upgrade.
db.version(11).stores({
  taxJurisdictions:      null,
  taxPayments:           null,
}).upgrade(async (_tx) => {
  // Stores dropped; nothing to migrate.
});

// ── v12: supplies (Amazon + any non-bank-detected supply purchases) ──────────
// One row per ORDER (not per line item) so dedupe by orderRef stays trivial.
// items[] holds the individual SKUs the import surfaced; null when the
// source only gave a single line. Sources today: pasted Claude-Web JSON
// produced via the "Copy extraction prompt" button; a CSV in the canonical
// format from samples/amazon.csv parses the same way.
db.version(12).stores({
  supplies: 'supplyId, userId, orderDate, vendor, sourceHash, [userId+orderDate]',
}).upgrade(async (_tx) => {
  // New table only.
});

// v13 — Spending tab: per-merchant cost-report allocation for card charges.
// Keyed by [userId+ruleKey] where ruleKey is the normalized merchant (or a
// '#<plaidTxId>' fallback for charges without a merchant). category is one of
// the SPEND_CATEGORIES ids in lib/cashflow/spendCategory.js.
db.version(13).stores({
  spendRule: '[userId+ruleKey], userId',
}).upgrade(async (_tx) => {
  // New table only.
});

export async function openDb() {
  if (!db.isOpen()) await db.open();
  return db;
}

/**
 * Wipe ALL local data. Used by sign-out and by the restore flow.
 * Returns nothing; throws on failure.
 */
export async function resetDb() {
  await db.close();
  await Dexie.delete('kanjo');
  // Re-open with a fresh schema.
  await db.open();
}
